import React from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import SpotifyEmbed from '../components/SpotifyEmbed';
import { useEnv } from '../contexts/EnvContext';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useNews } from '../hooks/useNews';
import { useNewsArticles } from '../hooks/useNewsArticles';
import PopupDev from '../components/PopupDev';
import type { NewsPost } from '../types/news';

// ---------------------------------------------------------------------------
// Helper: format a date string to a readable locale string
// ---------------------------------------------------------------------------
function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Helper: strip html-ish content and truncate
// ---------------------------------------------------------------------------
function excerpt(text: string, max = 130) {
  const clean = text.replace(/<[^>]+>/g, '').trim();
  return clean.length > max ? clean.slice(0, max) + '…' : clean;
}

// ---------------------------------------------------------------------------
// Static nyheter items (mirrors Nyheter.tsx)
// ---------------------------------------------------------------------------
// Removed — news is now fetched dynamically from useNewsArticles

// ---------------------------------------------------------------------------
// Reusable reveal wrapper — animates when scrolled into view
// ---------------------------------------------------------------------------
interface RevealProps {
  children: React.ReactNode;
  delay?: string; // Tailwind delay class e.g. 'delay-100'
  className?: string;
}

function Reveal({ children, delay = '', className = '' }: RevealProps) {
  const { ref, visible } = useScrollReveal(0.12);
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`transition-all duration-700 ease-out ${delay} ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section divider
// ---------------------------------------------------------------------------
function SectionDivider() {
  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(155,240,30,0.25), transparent)' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section label + heading
// ---------------------------------------------------------------------------
interface SectionHeaderProps {
  label: string;
  heading: string;
}
function SectionHeader({ label, heading }: SectionHeaderProps) {
  return (
    <Reveal>
      <p className="text-xs font-bold tracking-[0.25em] uppercase mb-3" style={{ color: '#9bf01e' }}>
        <span style={{ opacity: 0.5 }}>{'// '}</span>{label}
      </p>
      <h2 className="text-4xl sm:text-5xl font-extrabold text-white leading-none mb-10 tracking-tight">
        {heading}
      </h2>
    </Reveal>
  );
}

// ---------------------------------------------------------------------------
// Mini post card
// ---------------------------------------------------------------------------
interface PostCardProps {
  title: string;
  body: string;
  date?: string | null;
  delay?: string;
  to?: string;
}
function PostCard({ title, body, date, delay, to }: PostCardProps) {
  const inner = (
    <div
      className={`card-glow h-full flex flex-col gap-3 transition-all duration-200 group ${
        to ? 'cursor-pointer' : 'cursor-default'
      }`}
      style={{ borderLeft: '2px solid rgba(155,240,30,0.22)' }}
    >
      {date && (
        <span className="text-xs font-mono" style={{ color: '#9bf01e', opacity: 0.6 }}>{date}</span>
      )}
      <h3 className="text-base font-bold text-white group-hover:text-[#9bf01e] transition-colors duration-200">
        {title}
      </h3>
      <p className="text-sm leading-relaxed flex-grow" style={{ color: 'rgba(255,255,255,0.45)' }}>{body}</p>
      {to && (
        <span className="text-xs font-bold tracking-wider" style={{ color: 'rgba(155,240,30,0.55)' }}>Les mer →</span>
      )}
    </div>
  );
  if (to) {
    return (
      <Reveal delay={delay}>
        <Link to={to} className="block h-full">{inner}</Link>
      </Reveal>
    );
  }
  return <Reveal delay={delay}>{inner}</Reveal>;
}

// ---------------------------------------------------------------------------
// "See all" CTA link
// ---------------------------------------------------------------------------
interface CtaLinkProps {
  to: string;
  label: string;
}
function CtaLink({ to, label }: CtaLinkProps) {
  return (
    <Reveal delay="delay-300">
      <div className="mt-8 flex">
        <Link
          to={to}
          className="group inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase transition-all duration-200"
          style={{ color: 'rgba(255,255,255,0.45)' }}
          onMouseEnter={e => (e.currentTarget.style.color = '#9bf01e')}
          onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.45)')}
        >
          {label}
          <span className="transform group-hover:translate-x-1 transition-transform duration-200">
            →
          </span>
        </Link>
      </div>
    </Reveal>
  );
}

// ---------------------------------------------------------------------------
// Home page
// ---------------------------------------------------------------------------
const Home: React.FC = () => {
  const { isDev } = useEnv();
  const { posts: blogPosts, loading: blogLoading } = useNews();
  const { articles: newsArticles, loading: newsLoading } = useNewsArticles();

  // Pick only first 2 for each preview
  const latestBlogPosts: NewsPost[] = blogPosts.slice(0, 2);
  const latestNews = newsArticles.slice(0, 2);

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <Hero />

      {/* ── About ────────────────────────────────────────────────────────── */}
      <section className="section-padding" style={{ marginTop: '-4rem', position: 'relative', zIndex: 1 }}>
        <div className="container-responsive max-w-4xl">
          <SectionHeader label="Om oss" heading="Smuglergods" />
          <Reveal delay="delay-100">
            <p className="text-gray-300 text-lg leading-relaxed max-w-2xl mb-2">
              En lite nøye utplukka gjeng fra alle hedmarks kanter. En gjeng som kan veldig lite hver for seg, men som
              sammen kan bittelitt mindre enn det som er vanlig. 
            </p>
          </Reveal>
          <CtaLink to="/about" label="Hvem er vi" />
        </div>
      </section>

      {/* ── Music ────────────────────────────────────────────────────────── */}
      <SectionDivider />
      <section className="section-padding">
        <div className="container-responsive max-w-4xl">
          <SectionHeader label="Musikk" heading="Lytt nå" />
          <Reveal delay="delay-100">
            <p className="text-base leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Hør hele katalogen — fra smuglerclassics til siste slipp. Tilgjengelig på Spotify.
            </p>
            <SpotifyEmbed height={352} />
          </Reveal>
          <CtaLink to="/musikk" label="Se mer" />
        </div>
      </section>

      {/* ── Nyheter ──────────────────────────────────────────────────────── */}
      <SectionDivider />
      <section className="section-padding">
        <div className="container-responsive max-w-4xl">
          <SectionHeader label="Nyheter" heading="Siste nytt" />
          {newsLoading ? (
            <Reveal>
              <div className="flex gap-5">
                {[0, 1].map((i) => (
                  <div key={i} className="flex-1 h-36 rounded-lg bg-white bg-opacity-5 animate-pulse" />
                ))}
              </div>
            </Reveal>
          ) : latestNews.length === 0 ? (
            <Reveal>
              <p className="text-gray-500 italic text-sm">Ingen nyheter ennå.</p>
            </Reveal>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              {latestNews.map((article, i) => (
                <PostCard
                  key={article.id}
                  title={article.title}
                  body={excerpt(article.content)}
                  date={formatDate(article.created_at)}
                  delay={i === 0 ? 'delay-100' : 'delay-200'}
                  to={`/nyheter/${article.id}`}
                />
              ))}
            </div>
          )}
          <CtaLink to="/nyheter" label="Alle nyheter" />
        </div>
      </section>

      {/* ── Blog ─────────────────────────────────────────────────────────── */}
      <SectionDivider />
      <section className="section-padding">
        <div className="container-responsive max-w-4xl">
          <SectionHeader label="Blogg" heading="Siste innlegg" />

          {blogLoading ? (
            <Reveal>
              <div className="flex gap-5">
                {[0, 1].map((i) => (
                  <div
                    key={i}
                    className="flex-1 h-36 rounded-lg bg-white bg-opacity-5 animate-pulse"
                  />
                ))}
              </div>
            </Reveal>
          ) : latestBlogPosts.length === 0 ? (
            <Reveal>
              <p className="text-gray-500 italic text-sm">Ingen innlegg ennå.</p>
            </Reveal>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              {latestBlogPosts.map((post, i) => (
                <PostCard
                  key={post.id}
                  title={post.title}
                  body={excerpt(post.content)}
                  date={formatDate(post.created_at)}
                  delay={i === 0 ? 'delay-100' : 'delay-200'}
                  to={`/blog/${post.id}`}
                />
              ))}
            </div>
          )}

          <CtaLink to="/blog" label="Les bloggen" />
        </div>
      </section>

      <SectionDivider />

      {/* Dev tooling */}
      {isDev && <PopupDev />}
    </>
  );
};

export default Home;
